// ---------------------------------------------------------------------------
// Local-first Android startup support.
//
// The APK now ships the production web assets inside itself, so the WebView
// loads the app shell from `https://localhost` (Capacitor's local asset
// server) instead of downloading HTML+JS from the internet on every cold
// start. First paint therefore happens with ZERO network.
//
// Consequence: anything the app used to request with a same-origin relative
// URL (TanStack Start server functions under /_serverFn, server routes under
// /api) would now hit the local asset server and 404. This module rewrites
// those requests — and only those — to the deployed origin. Supabase calls are
// already absolute and are untouched.
// ---------------------------------------------------------------------------

/** Deployed origin that serves server functions / API routes. */
export const REMOTE_ORIGIN = "https://brothersclub.co.in";

/** Paths that must always be answered by the deployed server, not the bundle. */
const REMOTE_PATH_PREFIXES = ["/_serverFn", "/api/"];

let installed = false;

function isNative(): boolean {
  try {
    const cap = (window as any).Capacitor;
    return !!(cap && typeof cap.isNativePlatform === "function" && cap.isNativePlatform());
  } catch {
    return false;
  }
}

/**
 * True when the WebView is rendering assets bundled inside the APK
 * (capacitor:// or https://localhost) rather than the deployed site.
 */
export function isLocalBundle(): boolean {
  if (typeof window === "undefined") return false;
  if (!isNative()) return false;
  const { protocol, hostname } = window.location;
  return protocol === "capacitor:" || hostname === "localhost" || hostname === "127.0.0.1";
}

function needsRemote(pathname: string): boolean {
  return REMOTE_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(p));
}

function rewrite(input: string): string {
  try {
    const url = new URL(input, window.location.href);
    if (url.origin !== window.location.origin) return input;
    if (!needsRemote(url.pathname)) return input;
    return `${REMOTE_ORIGIN}${url.pathname}${url.search}${url.hash}`;
  } catch {
    return input;
  }
}

/**
 * Absolute base URL for backend calls. On the web build this is the current
 * origin; inside the APK bundle it is always the deployed origin.
 * Prefer `apiUrl("/api/...")` over relying on the fetch patch.
 */
export function apiBase(): string {
  if (typeof window === "undefined") return REMOTE_ORIGIN;
  return isLocalBundle() ? REMOTE_ORIGIN : window.location.origin;
}

/** Build an absolute backend URL for a relative path. */
export function apiUrl(path: string): string {
  return `${apiBase()}${path.startsWith("/") ? path : `/${path}`}`;
}

function log(original: string, final: string, outcome: string) {
  // Diagnostics only: URLs and status. Never bodies, tokens or OTP codes.
  console.info(`[native-api] ${original} -> ${final} :: ${outcome}`);
}

/**
 * Patches `fetch` (and XHR) so server-function / API calls made from the
 * bundled shell reach the deployed backend. Idempotent, no-op on web.
 */
export function installNativeApiProxy(): void {
  if (installed) return;
  if (typeof window === "undefined") return;
  if (!isLocalBundle()) return;
  installed = true;

  const originalFetch = window.fetch.bind(window);
  window.fetch = (async (input: RequestInfo | URL, init?: RequestInit) => {
    let originalUrl = "";
    let finalUrl = "";
    try {
      let target: RequestInfo | URL = input;
      if (typeof input === "string" || input instanceof URL) {
        originalUrl = String(input);
        finalUrl = rewrite(originalUrl);
        target = finalUrl;
      } else {
        const req = input as Request;
        originalUrl = req.url;
        finalUrl = rewrite(req.url);
        target = finalUrl === req.url ? req : new Request(finalUrl, req);
      }
      if (originalUrl === finalUrl) return await originalFetch(target as any, init);
      try {
        const res = await originalFetch(target as any, init);
        log(originalUrl, finalUrl, `HTTP ${res.status}`);
        return res;
      } catch (err: any) {
        log(originalUrl, finalUrl, `network-failure: ${err?.name ?? "Error"}`);
        throw err;
      }
    } catch (err) {
      if (finalUrl && originalUrl !== finalUrl) throw err;
      return originalFetch(input as any, init);
    }
  }) as typeof window.fetch;

  // Some libraries (and legacy code paths) still use XMLHttpRequest.
  const XHR = window.XMLHttpRequest;
  if (XHR) {
    const originalOpen = XHR.prototype.open;
    XHR.prototype.open = function (
      this: XMLHttpRequest,
      method: string,
      url: string | URL,
      ...rest: any[]
    ) {
      let next = String(url);
      try {
        const rewritten = rewrite(next);
        if (rewritten !== next) {
          log(next, rewritten, "xhr");
          next = rewritten;
        }
      } catch {
        /* ignore */
      }
      return (originalOpen as any).call(this, method, next, ...rest);
    } as any;
  }

  console.info("[native] local bundle active — API calls proxied to", REMOTE_ORIGIN);
}

